<script setup>
import { ref } from 'vue';
import { onClickOutside } from '@vueuse/core';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  options: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: 'Selecionar...',
  },
  align: {
    type: String,
    default: 'left',
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const isOpen = ref(false);
const dropdownRef = ref(null);

onClickOutside(dropdownRef, () => {
  isOpen.value = false;
});

const selectedOption = () => {
  return props.options.find(opt => String(opt.value) === String(props.modelValue));
};

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const select = option => {
  emit('update:modelValue', option.value);
  emit('change', option.value);
  isOpen.value = false;
};
</script>

<template>
  <div ref="dropdownRef" class="relative inline-block text-left select-none">
    <!-- Trigger Button -->
    <button
      type="button"
      class="h-8 px-2.5 inline-flex items-center gap-2 bg-n-alpha-1 hover:bg-n-alpha-2 active:bg-n-alpha-3 border border-n-weak rounded-lg text-xs font-medium text-n-slate-12 transition-colors cursor-pointer select-none focus:outline-none focus:border-brand shadow-xs"
      :class="{ 'border-brand bg-n-surface-1 ring-1 ring-brand': isOpen }"
      @click="toggle"
    >
      <!-- Optional Prefix Icon Slot or Default Icon -->
      <slot name="icon" />

      <!-- Selected Item Color Dot (if available and no slot icon) -->
      <span
        v-if="selectedOption()?.color && !$slots.icon"
        class="w-2 h-2 rounded-full shrink-0"
        :style="{ backgroundColor: selectedOption().color }"
      />

      <!-- Selected Label / Placeholder -->
      <span class="truncate font-medium text-n-slate-12 max-w-[150px]">
        {{ selectedOption() ? selectedOption().label : placeholder }}
      </span>

      <!-- Dropdown Chevron Arrow -->
      <svg
        class="w-3 h-3 text-n-slate-10 shrink-0 transition-transform duration-200"
        :class="{ 'rotate-180 text-n-brand': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <!-- Floating Dropdown Menu -->
    <div
      v-if="isOpen"
      class="absolute mt-1.5 min-w-[220px] max-w-[300px] max-h-[320px] overflow-y-auto bg-n-surface-1 border border-n-weak rounded-xl shadow-2xl p-1 z-50 animate-in fade-in zoom-in-95 duration-100 text-n-slate-12"
      :class="align === 'right' ? 'right-0' : 'left-0'"
    >
      <div class="space-y-0.5">
        <button
          v-for="opt in options"
          :key="opt.value"
          type="button"
          class="w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center justify-between group cursor-pointer"
          :class="
            String(opt.value) === String(modelValue)
              ? 'bg-brand-subtle text-n-brand font-semibold border border-brand/20'
              : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
          "
          @click="select(opt)"
        >
          <div class="flex items-center gap-2 truncate">
            <!-- Item icon or color dot -->
            <span
              v-if="opt.color"
              class="w-2 h-2 rounded-full shrink-0"
              :style="{ backgroundColor: opt.color }"
            />
            <span class="truncate">{{ opt.label }}</span>
          </div>

          <div class="flex items-center gap-1.5 shrink-0">
            <!-- Checkmark for selected item -->
            <svg
              v-if="String(opt.value) === String(modelValue)"
              class="w-3.5 h-3.5 text-n-brand"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </button>

        <div v-if="options.length === 0" class="p-3 text-center text-xs text-n-slate-10">
          Nenhuma opção disponível
        </div>
      </div>
    </div>
  </div>
</template>
